/**
 * Query Key Factory
 * 
 * SOURCE OF TRUTH for all React Query keys.
 * Provides type-safe, consistent query keys across the application.
 * 
 * Usage:
 * ```typescript
 * import { queryKeys } from '@/lib/queryKeys';
 * 
 * useQuery({
 *   queryKey: queryKeys.users.detail(userId),
 *   queryFn: () => fetchUser(userId),
 * });
 * ```
 */

export const queryKeys = {
  // User-related queries
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    profile: (id: string) => [...queryKeys.users.all, 'profile', id] as const,
  },

  // Expert-related queries
  experts: {
    all: ['experts'] as const,
    topByLocation: (limit?: number, offset?: number) => 
      [...queryKeys.experts.all, 'top-by-location', { limit, offset }] as const,
  },

  // Publication/content queries
  publications: {
    all: ['publications'] as const,
    lists: () => [...queryKeys.publications.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.publications.lists(), filters] as const,
    details: () => [...queryKeys.publications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.publications.details(), id] as const,
    bySlug: (slug: string) => [...queryKeys.publications.all, 'slug', slug] as const,
    withAuthors: (filters?: { locationId?: string | null; limit?: number; diversityMode?: string }) =>
      [...queryKeys.publications.all, 'with-authors', filters] as const,
  },

  // AI-related queries
  ai: {
    all: ['ai'] as const,
    models: () => [...queryKeys.ai.all, 'models'] as const,
    agents: () => [...queryKeys.ai.all, 'agents'] as const,
    agent: (id: string) => [...queryKeys.ai.agents(), id] as const,
    analytics: (filters?: object) => [...queryKeys.ai.all, 'analytics', filters] as const,
    sessions: (filters?: object) => [...queryKeys.ai.all, 'sessions', filters] as const,
  },

  // Job/background task queries
  jobs: {
    all: ['jobs'] as const,
    lists: () => [...queryKeys.jobs.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.jobs.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.jobs.all, 'detail', id] as const,
    stats: () => [...queryKeys.jobs.all, 'stats'] as const,
    bundles: () => [...queryKeys.jobs.all, 'bundles'] as const,
  },

  // Localization queries
  localizations: {
    all: ['localizations'] as const,
    keys: (namespace?: string) => [...queryKeys.localizations.all, 'keys', namespace] as const,
    stats: () => [...queryKeys.localizations.all, 'stats'] as const,
    locales: () => [...queryKeys.localizations.all, 'locales'] as const,
  },

  // Location queries
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.locations.lists(), filters] as const,
    detail: (id: number) => [...queryKeys.locations.all, 'detail', id] as const,
    hierarchy: () => [...queryKeys.locations.all, 'hierarchy'] as const,
  },

  // Topic queries
  topics: {
    all: ['topics'] as const,
    lists: () => [...queryKeys.topics.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.topics.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.topics.all, 'detail', id] as const,
    tree: () => [...queryKeys.topics.all, 'tree'] as const,
  },

  // Data source queries
  dataSources: {
    all: ['data-sources'] as const,
    lists: () => [...queryKeys.dataSources.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.dataSources.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.dataSources.all, 'detail', id] as const,
    values: (sourceId: string) => [...queryKeys.dataSources.all, 'values', sourceId] as const,
  },

  // Help center queries
  helpCenter: {
    all: ['help-center'] as const,
    tickets: (filters?: object) => [...queryKeys.helpCenter.all, 'tickets', filters] as const,
    ticket: (id: string) => [...queryKeys.helpCenter.all, 'ticket', id] as const,
    faqs: (filters?: object) => [...queryKeys.helpCenter.all, 'faqs', filters] as const,
  },

  // Analytics queries
  analytics: {
    all: ['analytics'] as const,
    events: (filters?: object) => [...queryKeys.analytics.all, 'events', filters] as const,
    dashboard: (range?: string) => [...queryKeys.analytics.all, 'dashboard', range] as const,
  },

  // Settings/config queries
  settings: {
    all: ['settings'] as const,
    system: () => [...queryKeys.settings.all, 'system'] as const,
    user: (userId: string) => [...queryKeys.settings.all, 'user', userId] as const,
  },

  // Favorites queries
  favorites: {
    all: ['favorites'] as const,
    publications: (userId?: string) => [...queryKeys.favorites.all, 'publications', userId] as const,
    experts: (userId?: string) => [...queryKeys.favorites.all, 'experts', userId] as const,
    unified: (userId?: string) => [...queryKeys.favorites.all, 'unified', userId] as const,
    publicationDetails: (publicationIds?: string[]) => [...queryKeys.favorites.all, 'publication-details', publicationIds] as const,
    expertDetails: (expertIds?: string[]) => [...queryKeys.favorites.all, 'expert-details', expertIds] as const,
  },

  // Membership queries
  memberships: {
    all: ['memberships'] as const,
    lists: () => [...queryKeys.memberships.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.memberships.lists(), filters] as const,
    counts: () => [...queryKeys.memberships.all, 'counts'] as const,
    dataQualityCounts: () => [...queryKeys.memberships.all, 'data-quality-counts'] as const,
    mine: () => [...queryKeys.memberships.all, 'mine'] as const,
  },

  // Order queries
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters?: object) => [...queryKeys.orders.lists(), filters] as const,
    counts: () => [...queryKeys.orders.all, 'counts'] as const,
    associationCounts: () => [...queryKeys.orders.all, 'association-counts'] as const,
    mine: () => [...queryKeys.orders.all, 'mine'] as const,
  },

  // MailerLite queries
  mailerlite: {
    all: ['mailerlite'] as const,
    groups: () => [...queryKeys.mailerlite.all, 'groups'] as const,
    syncHistory: () => [...queryKeys.mailerlite.all, 'sync-history'] as const,
    syncRunning: () => [...queryKeys.mailerlite.all, 'sync-running'] as const,
    comparison: () => [...queryKeys.mailerlite.all, 'comparison'] as const,
  },
} as const;

/**
 * Helper type to extract query key types
 */
export type QueryKeys = typeof queryKeys;
